import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import COLLEGES from "../lib/us-colleges.json";

/**
 * CollegeSelect, a searchable dropdown of accredited US colleges.
 *
 * Drop-in replacement for any <input> that asked the user to type a school
 * name. The selected value is a plain string and is propagated to the
 * parent through `onChange(name)` exactly the way a normal text input
 * would, so callers do not need to change anything else.
 *
 * Users can also free-type if their school is somehow not in the list,
 * the value committed will be whatever is in the input box on blur or
 * when they hit Enter.
 */
export default function CollegeSelect({
  value = "",
  onChange,
  placeholder = "Search any US college",
  className = "",
  inputClassName = "input",
  testId = "college-select",
  autoFocus = false,
  disabled = false,
}) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  // Keep input in sync if parent updates `value` externally.
  useEffect(() => { setQ(value || ""); }, [value]);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return COLLEGES.slice(0, 50);
    // Prefix matches first, then substring matches, capped at 50 for perf.
    const prefix = [];
    const sub = [];
    for (let i = 0; i < COLLEGES.length; i++) {
      const name = COLLEGES[i];
      const lower = name.toLowerCase();
      if (lower.startsWith(needle)) prefix.push(name);
      else if (lower.includes(needle)) sub.push(name);
      if (prefix.length >= 50) break;
    }
    return prefix.concat(sub).slice(0, 50);
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Reset highlight when results change.
  useEffect(() => { setHighlight(0); }, [q, open]);

  const commit = (name) => {
    setQ(name);
    onChange?.(name);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (matches[highlight]) {
        e.preventDefault();
        commit(matches[highlight]);
      } else {
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Scroll highlighted row into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${highlight}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  return (
    <div ref={wrapRef} className={`relative w-full ${className}`} data-testid={testId}>
      <div className="relative">
        <Search
          className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "#9ca3af" }}
          aria-hidden
        />
        <input
          type="text"
          className={inputClassName}
          style={{ paddingLeft: 36, paddingRight: 32 }}
          value={q}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            onChange?.(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={() => onChange?.(q)}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          data-testid={`${testId}-input`}
        />
        <ChevronDown
          className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "#9ca3af" }}
          aria-hidden
        />
      </div>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 mt-1 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto fade-in"
          style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
          data-testid={`${testId}-list`}
        >
          {matches.length === 0 ? (
            <li
              className="px-4 py-3 text-sm"
              style={{ color: "#6b7280" }}
              data-testid={`${testId}-empty`}
            >
              No US college matched "{q}". Keep typing, your selection will still be used as entered.
            </li>
          ) : matches.map((name, i) => (
            <li key={name} data-index={i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); commit(name); }}
                onMouseEnter={() => setHighlight(i)}
                className="w-full text-left px-4 py-2.5 text-sm transition-colors"
                style={{
                  background: i === highlight ? "#eff6ff" : "transparent",
                  color: "#111928",
                  borderBottom: "1px solid #f3f4f6",
                }}
                data-testid={`${testId}-option-${i}`}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
